import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Plus, Edit2, Trash2, Search, Eye, CheckCircle,
  AlertCircle, Loader, X, Check, Building2, User, Phone,
  Mail, RotateCcw, Filter
} from 'lucide-react';
import { getClients, createClient, updateClient, deleteClient } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Clients.css';

const fmt = (n) => n || n === 0 ? new Intl.NumberFormat('fr-FR').format(n) + ' XOF' : '0 XOF';

const EMPTY_FORM = {
  type: 'particulier', nom: '', telephone: '', email: '', adresse: '',
  ninea: '', rccm: '', limiteCredit: '',
  representant: { nom:'', prenom:'', telephone:'', email:'', poste:'' },
};

export default function Clients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = ['admin-entreprise', 'admin-boutique', 'admin-pro', 'super-admin'].includes(user?.role);

  const [clients,   setClients]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [submitting,setSubmitting]= useState(false);
  const [toast,     setToast]     = useState(null);

  const [search,    setSearch]    = useState('');

  const [showModal,     setShowModal]     = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData,      setFormData]      = useState(EMPTY_FORM);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => { loadClients(); }, []); // eslint-disable-line

  const loadClients = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.searchTerm = search.trim();
      const res = await getClients(params);
      setClients(res.data.data || []);
    } catch { showToast('error', 'Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const nbEntreprises = useMemo(() => clients.filter(c => c.type === 'entreprise').length,          [clients]);
  const nbParticuliers= useMemo(() => clients.filter(c => c.type !== 'entreprise').length,          [clients]);

  const openCreate = () => { setEditingClient(null); setFormData(EMPTY_FORM); setShowModal(true); };
  const openEdit   = (c) => {
    setEditingClient(c);
    setFormData({
      type:        c.type || 'particulier',
      nom:         c.nom,
      telephone:   c.telephone,
      email:       c.email       || '',
      adresse:     c.adresse     || '',
      ninea:       c.ninea       || '',
      rccm:        c.rccm        || '',
      limiteCredit:c.limiteCredit != null ? c.limiteCredit : '',
      representant:{
        nom:       c.representant?.nom       || '',
        prenom:    c.representant?.prenom    || '',
        telephone: c.representant?.telephone || '',
        email:     c.representant?.email     || '',
        poste:     c.representant?.poste     || '',
      },
    });
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditingClient(null); setFormData(EMPTY_FORM); };

  const setRep = (field, val) =>
    setFormData(f => ({ ...f, representant: { ...f.representant, [field]: val } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingClient) {
        await updateClient(editingClient._id, formData);
        showToast('success', 'Client modifié avec succès');
      } else {
        await createClient(formData);
        showToast('success', 'Client créé avec succès');
      }
      closeModal();
      loadClients();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Une erreur est survenue');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (c) => {
    if (c.detteActuelle > 0) {
      showToast('error', 'Impossible de supprimer un client avec des dettes en cours');
      return;
    }
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await deleteClient(c._id);
      showToast('success', 'Client supprimé');
      loadClients();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
  };

  if (loading) return (
    <div className="cl-loading"><Loader className="cl-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="cl-page">

      {/* Toast */}
      {toast && (
        <div className={`cl-toast cl-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="cl-header">
        <div className="cl-header-left">
          <div className="cl-header-icon"><Users size={20}/></div>
          <div>
            <h1 className="cl-header-title">Clients</h1>
            <p className="cl-header-sub">Gestion des clients et crédits</p>
          </div>
        </div>
        <button className="cl-new-btn" onClick={openCreate}><Plus size={15}/> Nouveau client</button>
      </div>

      <div className="cl-content">

        {/* KPI */}
        <div className="cl-kpi-row">
          {[
            { label: 'Total clients',   value: clients.length,   color:'#6366f1', bg:'#eef2ff', icon: Users      },
            { label: 'Entreprises',     value: nbEntreprises,    color:'#3b82f6', bg:'#eff6ff', icon: Building2  },
            { label: 'Particuliers',    value: nbParticuliers,   color:'#0d9488', bg:'#f0fdfa', icon: User       },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="cl-kpi" style={{ borderTopColor: k.color }}>
                <div className="cl-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                <div className="cl-kpi-val">{k.value}</div>
                <div className="cl-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="cl-filters">
          <Filter size={13} style={{ color:'#94a3b8', flexShrink:0 }}/>
          <div className="cl-search-wrap">
            <Search size={13} className="cl-search-icon"/>
            <input
              className="cl-search" type="text" placeholder="Nom, téléphone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadClients()}/>
          </div>
          <button className="cl-search-btn" onClick={loadClients}>
            <Search size={13}/> Rechercher
          </button>
          <button className="cl-reset-btn" onClick={() => { setSearch(''); }}>
            <RotateCcw size={13}/>
          </button>
          <span className="cl-count">{clients.length} client{clients.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        {clients.length === 0 ? (
          <div className="cl-empty">
            <div className="cl-empty-icon"><Users size={32}/></div>
            <h3>Aucun client trouvé</h3>
            <p>Créez votre premier client ou modifiez les filtres</p>
          </div>
        ) : (
          <div className="cl-card">
            <table className="cl-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Téléphone</th>
                  <th>Email</th>
                  <th>Avoir</th>
                  <th>Limite crédit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => {
                  return (
                    <tr key={c._id}>
                      <td>
                        <div className="cl-client-cell">
                          <div className={`cl-av ${c.type === 'entreprise' ? 'cl-av--ent' : ''}`}>
                            {c.type === 'entreprise' ? <Building2 size={14}/> : <User size={14}/>}
                          </div>
                          <div>
                            <div className="cl-client-name">{c.nom}</div>
                            {c.type === 'entreprise' && c.representant?.nom && (
                              <div className="cl-client-rep">
                                Rep. : {c.representant.prenom} {c.representant.nom}
                                {c.representant.poste && ` — ${c.representant.poste}`}
                              </div>
                            )}
                            {c.ninea && <div className="cl-client-ninea">NINEA : {c.ninea}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="cl-contact-cell">
                          <Phone size={11}/> {c.telephone}
                          {c.type === 'entreprise' && c.representant?.telephone && (
                            <div className="cl-contact-sub">Rep. : {c.representant.telephone}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        {c.email
                          ? <div className="cl-contact-cell"><Mail size={11}/> {c.email}</div>
                          : <span className="cl-muted">—</span>}
                      </td>
                      <td>
                        {c.avoirDisponible > 0
                          ? <span className="cl-avoir-badge" title="Avoir disponible (issu d'un retour produit)">{fmt(c.avoirDisponible)}</span>
                          : <span className="cl-muted">—</span>}
                      </td>
                      <td>
                        {c.limiteCredit != null
                          ? <span className="cl-limite">{fmt(c.limiteCredit)}</span>
                          : <span className="cl-illimite">Illimité</span>}
                      </td>
                      <td>
                        <div className="cl-row-actions">
                          <button className="cl-btn cl-btn--view"   title="Voir détails" onClick={() => navigate(`/clients/${c._id}`)}><Eye size={13}/></button>
                          <button className="cl-btn cl-btn--edit"   title="Modifier"     onClick={() => openEdit(c)}><Edit2 size={13}/></button>
                          {canDelete && (
                            <button className="cl-btn cl-btn--danger" title="Supprimer"    onClick={() => handleDelete(c)}><Trash2 size={13}/></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════════ MODAL ══════════════ */}
      {showModal && (
        <>
          <div className="cl-overlay" onClick={closeModal}/>
          <div className="cl-modal">
            <div className="cl-modal-head">
              <div>
                <h2 className="cl-modal-title">
                  {editingClient ? <><Edit2 size={15}/> Modifier le client</> : <><Plus size={15}/> Nouveau client</>}
                </h2>
                {editingClient && <p className="cl-modal-sub">{editingClient.nom}</p>}
              </div>
              <button className="cl-modal-close" onClick={closeModal}><X size={16}/></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="cl-modal-body">

                {/* Type toggle */}
                <div className="cl-type-toggle">
                  {[['particulier', User, 'Particulier'], ['entreprise', Building2, 'Entreprise']].map(([val, Icon, lbl]) => (
                    <button key={val} type="button"
                      className={`cl-type-btn ${formData.type === val ? 'cl-type-btn--active' : ''}`}
                      onClick={() => setFormData({ ...formData, type: val })}>
                      <Icon size={14}/> {lbl}
                    </button>
                  ))}
                </div>

                {/* Nom */}
                <div className="cl-field">
                  <label>{formData.type === 'entreprise' ? 'Raison sociale *' : 'Nom complet *'}</label>
                  <input type="text" required autoFocus
                    placeholder={formData.type === 'entreprise' ? 'SARL Diallo Commerce' : 'Mamadou Diallo'}
                    value={formData.nom} onChange={e => setFormData({...formData, nom: e.target.value})}/>
                </div>

                {/* Champs entreprise */}
                {formData.type === 'entreprise' && (
                  <>
                    <div className="cl-section-box cl-section-box--blue">
                      <div className="cl-section-box-title"><Building2 size={13}/> Informations entreprise</div>
                      <div className="cl-form-row2">
                        <div className="cl-field">
                          <label>NINEA</label>
                          <input type="text" placeholder="001234567 2Z3"
                            value={formData.ninea} onChange={e => setFormData({...formData, ninea: e.target.value})}/>
                        </div>
                        <div className="cl-field">
                          <label>RCCM</label>
                          <input type="text" placeholder="SN-DKR-2023-B-12345"
                            value={formData.rccm} onChange={e => setFormData({...formData, rccm: e.target.value})}/>
                        </div>
                      </div>
                    </div>

                    <div className="cl-section-box">
                      <div className="cl-section-box-title"><User size={13}/> Représentant</div>
                      <div className="cl-form-row2">
                        <div className="cl-field">
                          <label>Prénom</label>
                          <input type="text" placeholder="Prénom"
                            value={formData.representant.prenom} onChange={e => setRep('prenom', e.target.value)}/>
                        </div>
                        <div className="cl-field">
                          <label>Nom</label>
                          <input type="text" placeholder="Nom"
                            value={formData.representant.nom} onChange={e => setRep('nom', e.target.value)}/>
                        </div>
                        <div className="cl-field">
                          <label>Téléphone direct</label>
                          <input type="tel" placeholder="77 000 00 00"
                            value={formData.representant.telephone} onChange={e => setRep('telephone', e.target.value)}/>
                        </div>
                        <div className="cl-field">
                          <label>Email direct</label>
                          <input type="email" placeholder="rep@societe.com"
                            value={formData.representant.email} onChange={e => setRep('email', e.target.value)}/>
                        </div>
                      </div>
                      <div className="cl-field">
                        <label>Fonction / Poste</label>
                        <input type="text" placeholder="Directeur Général, DAF…"
                          value={formData.representant.poste} onChange={e => setRep('poste', e.target.value)}/>
                      </div>
                    </div>
                  </>
                )}

                {/* Téléphone + Email */}
                <div className="cl-form-row2">
                  <div className="cl-field">
                    <label>{formData.type === 'entreprise' ? 'Téléphone siège *' : 'Téléphone *'}</label>
                    <input type="tel" required placeholder="77 123 45 67"
                      value={formData.telephone}
                      onChange={e => setFormData({...formData, telephone: e.target.value})}/>
                  </div>
                  <div className="cl-field">
                    <label>Email <span className="cl-opt">(optionnel)</span></label>
                    <input type="email" placeholder="contact@example.com"
                      value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}/>
                  </div>
                </div>

                {/* Adresse */}
                <div className="cl-field">
                  <label>Adresse <span className="cl-opt">(optionnel)</span></label>
                  <textarea rows="2" placeholder="Adresse du client…"
                    value={formData.adresse} onChange={e => setFormData({...formData, adresse: e.target.value})}/>
                </div>

                {/* Limite crédit — illimité par défaut (limiteCredit vide = null côté serveur) */}
                <div className="cl-field">
                  <label className="cl-credit-toggle">
                    <input type="checkbox"
                      checked={formData.limiteCredit === '' || formData.limiteCredit == null}
                      onChange={e => setFormData({ ...formData, limiteCredit: e.target.checked ? '' : 0 })}/>
                    Crédit illimité
                  </label>
                  {formData.limiteCredit !== '' && formData.limiteCredit != null && (
                    <>
                      <label>Limite de crédit (XOF) <span className="cl-opt">— 0 = aucun crédit</span></label>
                      <input type="number" min="0" step="1000" required
                        value={formData.limiteCredit}
                        onChange={e => setFormData({...formData, limiteCredit: e.target.value === '' ? 0 : e.target.value})}/>
                    </>
                  )}
                </div>

              </div>

              <div className="cl-modal-footer">
                <button type="button" className="cl-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="cl-btn-confirm" disabled={submitting}>
                  {submitting
                    ? <><Loader size={13} className="cl-spin"/> Enregistrement…</>
                    : <><Check size={13}/> {editingClient ? 'Enregistrer' : 'Créer le client'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
